import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { exampleStories } from '../data/exampleStories';

const EmbeddedStoryView = () => {
  const { storyId } = useParams();
  const [passages, setPassages] = useState([]);
  const [currentPassage, setCurrentPassage] = useState('Start');
  const [slideDirection, setSlideDirection] = useState('');
  const [isAnimating, setIsAnimating] = useState(false);
  
  useEffect(() => {
    const story = exampleStories[storyId];
    if (!story) {
      return;
    }
    
    // Parse Twee
    const parsed = parseTwee(story.twee);
    setPassages(parsed);
    
    // Restore saved state after navigation (with delay to ensure passages are loaded)
    setTimeout(() => {
      const savedState = localStorage.getItem('demoState');
      if (savedState && savedState !== 'Start' && parsed.find(p => p.name === savedState)) {
        setCurrentPassage(savedState);
        // Keep state for 30 seconds, then clear
        setTimeout(() => {
          localStorage.removeItem('demoState');
        }, 30000);
      }
    }, 100);
  }, [storyId]);
  
  const navigateToPassage = (targetName, direction) => {
    if (isAnimating) return;
    
    setIsAnimating(true);
    setSlideDirection(direction);
    
    setTimeout(() => {
      setCurrentPassage(targetName);
      setSlideDirection('');
      setIsAnimating(false);
    }, 300);
  };
  
  const parseTwee = (twee) => {
    const passages = [];
    const lines = twee.split('\n');
    let currentPassage = null;
    
    for (let i = 0; i < lines.length; i++) {
      const line = lines[i];
      
      if (line.trim().startsWith(':: ')) {
        if (currentPassage) {
          passages.push(currentPassage);
        }
        const name = line.substring(3).split('[')[0].trim();
        currentPassage = { name, content: '', links: [] };
      } else if (currentPassage) {
        currentPassage.content += line + '\n';
      }
    }
    
    if (currentPassage) {
      passages.push(currentPassage);
    }
    
    // Process links for each passage
    passages.forEach(passage => {
      if (passage.name === 'StoryTitle') return;
      
      const linkRegex = /\[\[([^\]]+)\]\]/g;
      let match;
      
      while ((match = linkRegex.exec(passage.content)) !== null) {
        const linkText = match[1];
        let parts, label, target, position = 'default';
        
        if (linkText.includes('|')) {
          parts = linkText.split('|');
          label = parts[0].trim();
          target = parts[1].trim();
        } else if (linkText.includes('->')) {
          parts = linkText.split('->');
          label = parts[0].trim();
          target = parts[1].trim();
        } else {
          label = linkText.trim();
          target = linkText.trim();
        }
        
        // Check for positioning markers
        if (label.includes('{{{left}}}')) {
          position = 'left';
          label = label.replace('{{{left}}}', '').trim();
        } else if (label.includes('{{{right}}}')) {
          position = 'right';
          label = label.replace('{{{right}}}', '').trim();
        }
        
        // Check if it's a URL link
        const isUrl = target.startsWith('URL:');
        const finalTarget = isUrl ? target.substring(4) : target;
        
        passage.links.push({ label, target: finalTarget, isUrl, position });
      }
      
      // Remove links from content
      passage.content = passage.content.replace(linkRegex, '').trim();
    });
    
    return passages;
  };
  
  const processContent = (text) => {
    // Process bold (''text'')
    let processed = text.replace(/''([^']+)''/g, '<strong>$1</strong>');
    // Convert newlines to breaks
    processed = processed.replace(/\n{2,}/g, '<br><br>').replace(/\n/g, '<br>');
    return processed;
  };
  
  const handleLinkClick = (link, event) => {
    if (link.isUrl) {
      // Let the anchor tag handle navigation naturally
      return;
    } else {
      // Prevent default for internal navigation
      if (event) event.preventDefault();
      // Determine slide direction based on current passage
      const direction = currentPassage === 'Start' 
        ? (link.position === 'left' ? 'left' : 'right')
        : 'right'; // Back always slides right
      navigateToPassage(link.target, direction);
    }
  };
  
  const passage = passages.find(p => p.name === currentPassage);
  const storyTitle = passages.find(p => p.name === 'StoryTitle')?.content.trim() || 'Interactive Story';
  
  if (!passage) {
    return <div style={{ padding: '20px', textAlign: 'center' }}>Loading...</div>;
  }
  
  const leftLinks = passage.links.filter(l => l.position === 'left');
  const rightLinks = passage.links.filter(l => l.position === 'right');
  
  return (
    <div style={{
      fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", "Roboto", sans-serif',
      background: 'transparent',
      padding: '0',
      minHeight: '100%',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center'
    }}>
      <div style={{
        background: 'white',
        borderRadius: '12px',
        boxShadow: '0 10px 30px rgba(0, 0, 0, 0.15)',
        display: 'grid',
        gridTemplateColumns: '25% 50% 25%',
        gridTemplateRows: '1fr',
        gap: 0,
        width: '100%',
        height: '100%',
        padding: '0',
        overflow: 'hidden'
      }}>
        
        <div style={{
          gridColumn: 1,
          gridRow: 1,
          display: 'flex',
          flexDirection: 'column',
          gap: '12px',
          padding: '15px 8px',
          alignItems: 'center',
          justifyContent: 'center'
        }}>
          {leftLinks.map((link, idx) => {
            if (link.isUrl) {
              const baseUrl = window.location.origin;
              // Save state before navigation
              const handleUrlClick = (e) => {
                e.preventDefault();
                localStorage.setItem('demoState', currentPassage);
                // Navigate the entire window, not just iframe
                window.location.href = baseUrl + link.target;
              };
              return (
                <a
                  key={idx}
                  href={baseUrl + link.target}
                  onClick={handleUrlClick}
                  style={{
                    background: '#667eea',
                    color: 'white',
                    padding: '12px 14px',
                    borderRadius: '8px',
                    border: 'none',
                    fontSize: '0.85em',
                    cursor: 'pointer',
                    transition: 'all 0.2s',
                    width: '100%',
                    textAlign: 'center',
                    minHeight: '50px',
                    fontWeight: 500,
                    textDecoration: 'none',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}
                >
                  <span style={{ opacity: 0.6, marginRight: '4px' }}>←</span>
                  {link.label}
                </a>
              );
            } else {
              return (
                <button
                  key={idx}
                  onClick={(e) => handleLinkClick(link, e)}
                  style={{
                    background: '#667eea',
                    color: 'white',
                    padding: '12px 14px',
                    borderRadius: '8px',
                    border: 'none',
                    fontSize: '0.85em',
                    cursor: 'pointer',
                    transition: 'all 0.2s',
                    width: '100%',
                    textAlign: 'center',
                    minHeight: '50px',
                    fontWeight: 500
                  }}
                >
                  <span style={{ opacity: 0.6, marginRight: '4px' }}>←</span>
                  {link.label}
                </button>
              );
            }
          })}
        </div>
        
        <div style={{
          gridColumn: 2,
          gridRow: 1,
          padding: '20px',
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          textAlign: 'center',
          lineHeight: 1.6,
          color: '#2d3748',
          fontSize: '0.95em',
          position: 'relative',
          overflow: 'hidden'
        }}>
          <div 
            style={{
              animation: slideDirection ? `slide-${slideDirection} 0.3s ease-out` : 'none'
            }}
            dangerouslySetInnerHTML={{ __html: processContent(passage.content) }}
          />
        </div>
        
        <div style={{
          gridColumn: 3,
          gridRow: 1,
          display: 'flex',
          flexDirection: 'column',
          gap: '12px',
          padding: '15px 8px',
          alignItems: 'center',
          justifyContent: 'center'
        }}>
          {rightLinks.map((link, idx) => {
            if (link.isUrl) {
              const baseUrl = window.location.origin;
              // Save state before navigation
              const handleUrlClick = (e) => {
                e.preventDefault();
                localStorage.setItem('demoState', currentPassage);
                // Navigate the entire window, not just iframe
                window.location.href = baseUrl + link.target;
              };
              return (
                <a
                  key={idx}
                  href={baseUrl + link.target}
                  onClick={handleUrlClick}
                  style={{
                    background: '#667eea',
                    color: 'white',
                    padding: '12px 14px',
                    borderRadius: '8px',
                    border: 'none',
                    fontSize: '0.85em',
                    cursor: 'pointer',
                    transition: 'all 0.2s',
                    width: '100%',
                    textAlign: 'center',
                    minHeight: '50px',
                    fontWeight: 500,
                    textDecoration: 'none',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}
                >
                  {link.label}
                  <span style={{ opacity: 0.6, marginLeft: '4px' }}>→</span>
                </a>
              );
            } else {
              return (
                <button
                  key={idx}
                  onClick={(e) => handleLinkClick(link, e)}
                  style={{
                    background: '#667eea',
                    color: 'white',
                    padding: '12px 14px',
                    borderRadius: '8px',
                    border: 'none',
                    fontSize: '0.85em',
                    cursor: 'pointer',
                    transition: 'all 0.2s',
                    width: '100%',
                    textAlign: 'center',
                    minHeight: '50px',
                    fontWeight: 500
                  }}
                >
                  {link.label}
                  <span style={{ opacity: 0.6, marginLeft: '4px' }}>→</span>
                </button>
              );
            }
          })}
        </div>
      </div>
      
      <style>{`
        @keyframes slide-left {
          from { transform: translateX(0); opacity: 1; }
          to { transform: translateX(-100%); opacity: 0; }
        }
        @keyframes slide-right {
          from { transform: translateX(0); opacity: 1; }
          to { transform: translateX(100%); opacity: 0; }
        }
        button:hover {
          filter: brightness(0.9);
          transform: translateY(-2px);
        }
        strong {
          font-weight: 700;
          color: #667eea;
        }
      `}</style>
    </div>
  );
};

export default EmbeddedStoryView;
